import { useContext, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { STATUS_LABEL, type TaskStatus, type TaskWithFlags } from "@desk/contracts";
import type { TaskGroup, TaskListProps } from "../types";
import { StatusIcon } from "@/components/icons";
import { openQuickAdd } from "@/features/ui-state";
import { cn } from "@/lib/utils";
import { GroupHeader } from "./chrome";
import { GroupLabelContext, StatusGroupingContext } from "./context";

/** Linear's group order: started work first, then unstarted, then closed. */
const STATUS_ORDER: TaskStatus[] = ["in_progress", "todo", "backlog", "done", "cancelled"];

interface RenderGroup {
  key: string;
  label: string | null;
  icon?: ReactNode;
  status?: TaskStatus;
  variant: NonNullable<TaskGroup["variant"]>;
  dayNumber?: string;
  tasks: TaskWithFlags[];
  collapsed: boolean;
  onToggle?: () => void;
  trailing?: ReactNode;
  showWhenEmpty?: boolean;
  /** Original group label per task, kept as a chip when status grouping replaces the group. */
  labelOf?: Map<string, string | null>;
}

/** Width of the readable-ID column: the longest ID in this list, so IDs and status icons line up. */
function idWidth(groups: TaskGroup[]): string {
  let max = 6; // a common floor keeps Today's planned and due lists aligned
  for (const g of groups) for (const t of g.tasks) max = Math.max(max, t.readableId.length);
  return `${Math.min(max, 11) + 1.6}ch`;
}

export function ConsoleTaskList({ groups, renderTask, empty, footer, listProps }: TaskListProps) {
  const statusGrouping = useContext(StatusGroupingContext);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);
  const special = groups.some((g) => g.variant && g.variant !== "default");

  const rendered = useMemo<RenderGroup[]>(() => {
    if (!statusGrouping || special) {
      return groups.map((g) => ({
        key: g.key,
        label: g.label,
        icon: g.icon,
        variant: g.variant ?? "default",
        dayNumber: g.dayNumber,
        tasks: g.tasks,
        collapsed: g.collapsed ?? collapsed[g.key] ?? false,
        onToggle: g.onToggle,
        trailing: g.trailing,
        showWhenEmpty: g.showWhenEmpty,
      }));
    }
    // Regroup by status (Linear). The incoming grouping (project/space) survives as a row chip.
    const labelOf = new Map<string, string | null>();
    const byStatus = new Map<TaskStatus, TaskWithFlags[]>();
    const seen = new Set<string>();
    let closedGroup: TaskGroup | undefined;
    for (const g of groups) {
      if (g.collapsible && g.tasks.every((t) => t.status === "done" || t.status === "cancelled")) closedGroup = g;
      for (const t of g.tasks) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        labelOf.set(t.id, g.collapsible ? null : g.label);
        const list = byStatus.get(t.status) ?? [];
        list.push(t);
        byStatus.set(t.status, list);
      }
    }
    return STATUS_ORDER.filter((s) => byStatus.has(s)).map((s) => {
      const isClosed = s === "done" || s === "cancelled";
      const local = collapsed[s] ?? false;
      return {
        key: s,
        status: s,
        label: STATUS_LABEL[s],
        icon: <StatusIcon status={s} className="size-3.5" />,
        variant: "default" as const,
        tasks: byStatus.get(s)!,
        collapsed: isClosed && closedGroup ? !!closedGroup.collapsed : local,
        onToggle: isClosed && closedGroup?.onToggle ? closedGroup.onToggle : () => setCollapsed((c) => ({ ...c, [s]: !local })),
        labelOf,
      };
    });
  }, [groups, statusGrouping, special, collapsed]);

  const visible = rendered.filter((g) => g.tasks.length > 0 || g.showWhenEmpty);
  const style = { "--console-id-w": idWidth(groups) } as CSSProperties;

  return (
    <div data-console-list className={footer ? "pb-2" : undefined} style={style}>
      <div {...listProps} className="outline-none">
        {total === 0 && empty}
        {visible.map((g, i) => (
          <section key={g.key} aria-label={g.variant === "day" ? `${g.dayNumber} ${g.label}` : (g.label ?? undefined)}>
            <Header group={g} first={i === 0} />
            {!g.collapsed &&
              g.tasks.map((t) => (
                <GroupLabelContext.Provider key={t.id} value={g.labelOf?.get(t.id) ?? null}>
                  {renderTask(t)}
                </GroupLabelContext.Provider>
              ))}
          </section>
        ))}
      </div>
      {footer && <div className="mt-2 flex items-center [&_button]:h-7 [&_button]:text-[12.5px]">{footer}</div>}
    </div>
  );
}

function Header({ group: g, first }: { group: RenderGroup; first: boolean }) {
  if (g.variant === "day") {
    return (
      <div className={cn("-mx-4 flex h-10 items-end gap-2 border-b border-border px-6 pb-2", !first && "mt-3")}>
        <span className="min-w-[1.4em] font-display text-[17px] font-bold leading-none tracking-[-0.02em] text-foreground tabular">{g.dayNumber}</span>
        <span className="text-[13px] font-medium leading-none text-muted-foreground">{g.label}</span>
        {g.tasks.length > 0 && <span className="ml-auto text-[12px] leading-none text-subtle tabular">{g.tasks.length}</span>}
      </div>
    );
  }
  if (g.variant === "range" || g.variant === "month") {
    return (
      <div className={cn("-mx-4 flex h-10 items-end gap-2 border-b border-border px-6 pb-2", !first && "mt-4")}>
        <h2 className="font-display text-[14px] font-semibold leading-none tracking-[-0.01em] text-foreground">{g.label}</h2>
        {g.tasks.length > 0 && <span className="ml-auto text-[12px] leading-none text-subtle tabular">{g.tasks.length}</span>}
      </div>
    );
  }
  if (g.label === null) return null;
  return (
    <GroupHeader
      icon={g.icon}
      label={g.label}
      count={g.tasks.length}
      collapsed={g.collapsed}
      onToggle={g.onToggle}
      trailing={g.trailing}
      onAdd={g.status && g.status !== "done" && g.status !== "cancelled" ? () => openQuickAdd({ status: g.status }) : undefined}
      addLabel={g.status ? `New task in ${STATUS_LABEL[g.status]}` : undefined}
    />
  );
}
